import React, { useState } from 'react';
import { components } from 'react-select';
import { useI18n } from '@whizz/react-i18n';
import type { ControlProps, OptionTypeBase, SelectComponentsConfig, Props as SelectProps } from 'react-select';
import type { FieldGroupProps } from '../formGroup/types';
import type { ButtonContentAlignType } from '../button/types';
import type { AllColorType, AllIconType, SizeType } from '../../util/types';
import { DropdownMaskStyled, DropdownMenuStyled, DropdownWrapperStyled } from './Dropdown.styled';
import Button from '../button/Button';
import FieldGroup from '../formGroup/FieldGroup';
import Selector from '../selector/Selector';
import { CustomNoOptionsMessage } from '../selector/components';
import { DROPDOWN_WRAPPER } from '../../util/data-id';
import { GLOBAL_ICONS } from '../../util/vars';

type ComponentsType = keyof SelectComponentsConfig<OptionTypeBase, boolean>;

type Ref = HTMLDivElement;
interface Props extends Omit<SelectProps, 'value' | 'id'>, FieldGroupProps {
  /**
   * Sets a placeholder on the trigger button.
   */
  buttonPlaceholder?: string;
  /**
   * Sets a custom "no options" message.
   */
  searchNoOptions?: string;
  /**
   * Dropdown value based on react-select props.
   */
  value?: OptionTypeBase | undefined;
  /**
   * Prints a custom value on the button.
   */
  customValueToShow?: React.ReactNode | null;
  /**
   * Hides search input at menu dropdown.
   */
  disableSearch?: boolean;
  /**
   * Sets trigger button content align.
   */
  align?: ButtonContentAlignType;
  /**
   * Sets an icon to the left of the trigger button.
   */
  icon?: AllIconType;
  /**
   * Handles change on the dropdown.
   * @param option
   */
  handleChange(option: OptionTypeBase): void;
  /**
   * Pass custom components to select.
   */
  componentsForSelect?: Record<ComponentsType, JSX.Element>;
  /**
   * Adds a fixed border.
   * We need to do this to handle white dropdowns over white background.
   */
  forceBorder?: boolean;
  /**
   * Custom background color.
   */
  color?: AllColorType;
  /**
   * Sets trigger button size.
   */
  size?: SizeType;
  /**
   * Whether the component is full width of container
   */
  fullWidthOnMobile?: boolean;
}

const Dropdown = React.forwardRef<Ref, Props>((
  {
    id,
    label,
    hideLabel,
    labelStyle,
    isInline = false,
    buttonPlaceholder,
    searchNoOptions,
    value,
    customValueToShow,
    disableSearch = false,
    align = 'center',
    handleChange,
    icon,
    componentsForSelect,
    forceBorder = false,
    color = 'white',
    size = 'md',
    fullWidthOnMobile = false,
    ...other
  }: Props, ref): JSX.Element => {

  const { translate } = useI18n();

  const [ open, setOpen ] = useState(false);

  const ControlComponent = (props: ControlProps<OptionTypeBase, boolean>) => {
    return disableSearch ? null : <components.Control {...props} children={props.children} />;
  };

  const default_components = {
    Control: ControlComponent,
    IndicatorSeparator: null,
    DropdownIndicator: null,
    NoOptionsMessage: (props: SelectProps) => {
      return (
        <CustomNoOptionsMessage
          message={searchNoOptions ?? translate('components.SELECTORS.NO_OPTIONS.DEFAULT')}
          {...props}
        />
      );
    },
  };

  return (
    <FieldGroup id={id} label={label} hideLabel={hideLabel} labelStyle={labelStyle} isInline={isInline} fullWidthOnMobile={fullWidthOnMobile}>
      <DropdownWrapperStyled ref={ref} data-id={DROPDOWN_WRAPPER} data-selected={!!value} data-options={!!other.options?.length} $forceBorder={forceBorder}>
        <Button
          id={id}
          icon={icon}
          color={color}
          size={size}
          toggle={open}
          align={align}
          className={'w-full'}
          onClick={() => setOpen(!open)}
        >
          {value
            ? customValueToShow ?? value.label
            : buttonPlaceholder ?? translate('components.SELECTORS.PLACEHOLDERS.SINGLE')
          }
        </Button>
        {open && (
          <React.Fragment>
            {/* Select */}
            <DropdownMenuStyled $withSearch={!disableSearch} className={'z-max'}>
              <Selector
                id={`${id}_dropdown`}
                label={`${label} dropdown`}
                hideLabel
                size={'sm'}
                autoFocus
                menuIsOpen
                backspaceRemovesValue={false}
                controlShouldRenderValue={false}
                hideSelectedOptions={false}
                isClearable={false}
                tabSelectsValue={false}
                customComponents={Object.assign(default_components, componentsForSelect)}
                onChange={(option: OptionTypeBase) => {
                  setOpen(false);
                  handleChange(option);
                }}
                value={value}
                placeholder={translate('components.SELECTORS.DROPDOWN.SEARCH')}
                {...!disableSearch && {
                  icon: GLOBAL_ICONS.search,
                }}
                {...other as SelectProps}
              />
            </DropdownMenuStyled>
            {/* Mask */}
            <DropdownMaskStyled onClick={() => setOpen(false)} data-cy="classes-dropdown"/>
          </React.Fragment>
        )}
      </DropdownWrapperStyled>
    </FieldGroup>
  );
});

Dropdown.displayName = 'Dropdown';

export default Dropdown;
